import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Building2, Mail, Phone, MapPin } from "lucide-react";

/**
 * Ansvarig utgivare / Rättslig information (motsvarar tyskt Impressum och svenskt TT-avtal om
 * utgivaransvar för publicistisk verksamhet).
 */
export default function AnsvarigUtgivare() {
  useEffect(() => {
    document.title = "Ansvarig utgivare – vidalv.live";
    window.scrollTo(0, 0);
  }, []);

  return (
    <article data-testid="legal-ansvarigutgivare" className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
      <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-pine hover:text-pine-hover no-underline mb-6">
        <ArrowLeft size={14} /> Tillbaka till startsidan
      </Link>
      <h1 className="font-fraktur-smooth text-falu text-3xl sm:text-4xl mb-2">Ansvarig utgivare &amp; rättslig information</h1>
      <p className="text-xs text-bark/60 mb-8">Gäller från: 24 april 2026</p>

      <section className="space-y-6 text-ink leading-relaxed">
        <div className="bg-bone-alt/60 border border-warm-border rounded-sm p-5 sm:p-6">
          <h2 className="font-heading text-xl font-semibold text-ink mb-3 flex items-center gap-2">
            <Building2 size={20} /> Utgivare och ansvarig för plattformen
          </h2>
          <p className="mb-3">
            <strong>Hodema Edertools AB</strong><br />
            Organisationsnummer: <strong>556887-7699</strong><br />
            Registrerat hos Bolagsverket den 15 mars 2012<br />
            F-skatt och moms-registrerat i Sverige<br />
            Verksamhetens art: skogsskötsel, drivning, digital verktygsutveckling
          </p>
          <p className="flex items-start gap-2 mb-1.5">
            <MapPin size={16} className="mt-0.5 shrink-0 text-bark/70" />
            Kyrkvägen 66, 883 72 Backe, Sverige
          </p>
          <p className="flex items-center gap-2 mb-1.5">
            <Mail size={16} className="text-bark/70" />
            <a href="mailto:redaktion@vidalv.live" className="text-falu hover:text-falu-hover">redaktion@vidalv.live</a> (redaktion)<br />
          </p>
          <p className="flex items-center gap-2 ml-6 mb-1.5">
            <a href="mailto:info@hodema.se" className="text-falu hover:text-falu-hover">info@hodema.se</a> (bolag &amp; juridik)
          </p>
          <p className="flex items-center gap-2">
            <Phone size={16} className="text-bark/70" />
            <a href="tel:+46703660981" className="text-falu hover:text-falu-hover">+46 70 366 09 81</a>
          </p>
        </div>

        <div>
          <h2 className="font-heading text-xl font-semibold text-ink mb-2">Ansvarig utgivare</h2>
          <p>
            Som icke-registrerad publikation (ej tidskrift i PUL-mening) har vidalv.live ingen formellt
            utsedd ansvarig utgivare enligt Yttrandefrihetsgrundlagen. Redaktionellt ansvar för
            publicerat innehåll bärs av <strong>Hodema Edertools AB</strong> genom redaktionen.
          </p>
        </div>

        <div>
          <h2 className="font-heading text-xl font-semibold text-ink mb-2">Redaktionell policy</h2>
          <p>
            Vi strävar efter att spegla bygden så som den är: varierat, rättvist och med omsorg.
            Allt inskickat innehåll granskas innan publicering. Vi publicerar inte:
          </p>
          <ul className="list-disc pl-6 space-y-1 mt-2">
            <li>hets mot folkgrupp eller trakasserier,</li>
            <li>politisk eller religiös agitation,</li>
            <li>kommersiell reklam utanför kategorierna Företag/Köp &amp; sälj,</li>
            <li>osanna påståenden om namngivna personer utan faktaunderlag,</li>
            <li>material där upphovsrätten är oklar.</li>
          </ul>
          <p className="mt-3">
            Se också våra <Link to="/riktlinjer" className="text-falu">publiceringsriktlinjer</Link>.
          </p>
        </div>

        <div>
          <h2 className="font-heading text-xl font-semibold text-ink mb-2">Rättelser &amp; klagomål</h2>
          <p>
            Upptäcker du fel, tycker att något är osakligt eller vill ha något raderat?
            Skriv till <a href="mailto:redaktion@vidalv.live" className="text-falu">redaktion@vidalv.live</a>
            {" "}&ndash; vi svarar inom 5 vardagar och rättar snabbt om något är felaktigt.
          </p>
        </div>

        <div>
          <h2 className="font-heading text-xl font-semibold text-ink mb-2">Pressfrihet &amp; källkritik</h2>
          <p>
            vidalv.live är en privat publikation och omfattas inte av Tryckfrihetsförordningen eller
            meddelarskydd. Vi följer ändå svensk pressetik i tillämpliga delar (publicitetsregler från
            Pressens Opinionsnämnd/MEN).
          </p>
        </div>

        <div>
          <h2 className="font-heading text-xl font-semibold text-ink mb-2">Tvistlösning online</h2>
          <p>
            EU-kommissionens plattform för tvistlösning online:
            {" "}<a href="https://ec.europa.eu/consumers/odr" target="_blank" rel="noopener noreferrer" className="text-falu">ec.europa.eu/consumers/odr</a>.
            Vi deltar inte i obligatorisk skiljeförfarande hos ARN men besvarar klagomål gärna direkt.
          </p>
        </div>

        <div>
          <h2 className="font-heading text-xl font-semibold text-ink mb-2">Länkar &amp; tredje parts material</h2>
          <p>
            Externa länkar från vidalv.live öppnas med <code>rel=&quot;noopener&quot;</code>. Vi har ingen
            kontroll över externt innehåll och tar inget ansvar för det.
          </p>
          <p className="mt-2">
            Källor vi ofta refererar till: <a href="https://backe.nu" target="_blank" rel="noopener noreferrer" className="text-falu">backe.nu</a>,
            {" "}<a href="https://hembygd.se" target="_blank" rel="noopener noreferrer" className="text-falu">hembygd.se</a>,
            {" "}<a href="https://stromsund.se" target="_blank" rel="noopener noreferrer" className="text-falu">stromsund.se</a>,
            {" "}<a href="https://www.smhi.se" target="_blank" rel="noopener noreferrer" className="text-falu">SMHI</a>,
            {" "}<a href="https://www.openstreetmap.org" target="_blank" rel="noopener noreferrer" className="text-falu">OpenStreetMap</a>.
          </p>
        </div>

        <div className="pt-4 border-t border-warm-border/60">
          <p className="text-sm text-bark/70">
            Se även: <Link to="/integritetspolicy" className="text-falu">Integritetspolicy</Link> &middot;
            {" "}<Link to="/anvandarvillkor" className="text-falu">Användarvillkor</Link> &middot;
            {" "}<Link to="/cookiepolicy" className="text-falu">Cookiepolicy</Link>.
          </p>
        </div>
      </section>
    </article>
  );
}
